/**
 * MapaCalor — matriz categoría × columna con celdas teñidas y cifra dentro,
 * para paneles del portal (tema claro). Mismo tipo de gráfico y forma de
 * despliegue que el triage por hora de /tv/operacion (MapaCalorHora en
 * components/tv/piezas.tsx), pero con los tokens de color del portal en vez
 * de las variables --tv-* del tema oscuro de televisor.
 *
 * Cada categoría tiene SU fila con SU color y la columna es el bucket (hora,
 * día o semana) — no hay que descomponer segmentos apilados mentalmente. La
 * intensidad del tinte da el patrón de un vistazo y la cifra dentro da la
 * precisión exacta.
 */
import { Fragment } from 'react';
import s from './MapaCalor.module.css';

export interface FilaMapaCalor {
  clave: string;
  etiqueta: string;
  color: string;
  /** Un valor por columna, en el mismo orden que `columnas`. */
  valores: number[];
}

/** Transpone puntos por bucket (uno por columna, con una clave por serie) en
 *  filas por serie (una por categoría, con un valor por columna) — la forma
 *  que necesita `MapaCalor`. */
// eslint-disable-next-line react-refresh/only-export-components
export function filasDeMapa(
  puntos: Record<string, number | string>[],
  series: readonly { key: string; color: string }[],
): FilaMapaCalor[] {
  return series.map((se) => ({
    clave: se.key,
    etiqueta: se.key,
    color: se.color,
    valores: puntos.map((p) => Number(p[se.key] ?? 0)),
  }));
}

export function MapaCalor({ columnas, filas }: { columnas: string[]; filas: FilaMapaCalor[] }) {
  const max = Math.max(1, ...filas.flatMap((f) => f.valores));
  return (
    <div
      className={s.mapaCalor}
      style={{
        gridTemplateColumns: `auto repeat(${columnas.length}, 1fr)`,
        gridTemplateRows: `repeat(${filas.length}, minmax(0, 1fr)) auto`,
      }}
    >
      {filas.map((f) => {
        const total = f.valores.reduce((a, b) => a + b, 0);
        return (
          <Fragment key={f.clave}>
            <span className={s.mapaCalorEtiqueta}>
              <span className={s.mapaCalorNombre}>
                <span className={s.mapaCalorMarca} style={{ background: f.color }} />
                {f.etiqueta}
              </span>
              <b className={s.mapaCalorTotal}>{total}</b>
            </span>
            {f.valores.map((v, i) => (
              <span
                key={i}
                className={s.mapaCalorCelda}
                style={v > 0
                  ? { background: `color-mix(in srgb, ${f.color} ${Math.round(16 + 44 * (v / max))}%, transparent)` }
                  : undefined}
                title={`${columnas[i]} · ${f.etiqueta}: ${v}`}
              >
                {v > 0 ? v : ''}
              </span>
            ))}
          </Fragment>
        );
      })}
      <span aria-hidden="true" />
      {columnas.map((c, i) => <span key={i} className={s.mapaCalorCol}>{c}</span>)}
    </div>
  );
}
